"use client";
import { HookFormTimeSelectDate } from "@cmp-src/form/TimeSelect";
import { IResource } from "@gettactic/api";
import { HookFormCheckboxInput } from "@gettactic/components/src/form/CheckboxInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { useDisclosure, useStandardApiHandler } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { getTimeDateObjectFromString } from "@gettactic/helpers/src/date-time/getTimeDateObjectFromString";
import {
  useAuthenticated,
  useInvalidateSchedules,
  useResourceReservations
} from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Lock,
  UserCheck,
  Users
} from "lucide-react";
import { formatInTimeZone } from "@gettactic/helpers/src/date-time/dateFnsTz";
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import { addDays } from "date-fns";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { PermissionTable } from "@/features/permissions/components/PermissionsTable";
import { SharedPermissionTable } from "@/features/permissions/components/SharedPermissionsTable";
import { ToggleIsApprovablePermission } from "@/features/permissions/components/ToggleIsApprovablePermission";
import { usePermissions } from "@/features/permissions/hooks/usePermissions";
import { useResourcesPermissions } from "@/features/permissions/hooks/useResourcesPermissions";
import { AddAssignmentButton } from "@/features/reserve/components/resources/tabs/access-restrictions/AddAssignmentButton";
import { ConfirmOperationModal } from "@/features/reserve/components/resources/tabs/access-restrictions/RestrictionsTab";
import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from "@/features/reserve/hooks/useResourceSettings";
import { api } from "@/lib/api/api";
import { normalizeDedicatedUsers } from "@/lib/dedicated-users";
import { areAll } from "@/lib/utils";
import {
  DEDICATE_LOOKAHEAD_DAYS,
  DedicateResourceWarning
} from "./DedicateResourceWarning";
import { DedicatedOwnersSection } from "./DedicatedOwnersSection";
import {
  getReservationCancelTargets,
  groupResourceReservations
} from "./groupResourceReservations";

interface AccessRestrictionsFormData {
  hasAdditionalAccess: boolean;
  startsOn: string;
  startsAt: Date;
  allUsers: boolean;
}

const STARTS_ON_ITEMS = [
  { id: "0", name: "Same Day" },
  { id: "-1", name: "1 Day Before" },
  { id: "-2", name: "2 Days Before" },
  { id: "-3", name: "3 Days Before" },
  { id: "-4", name: "4 Days Before" },
  { id: "-5", name: "5 Days Before" },
  { id: "-6", name: "6 Days Before" },
  { id: "-7", name: "1 Week Before" }
];

type AccessMode = "open-access" | "restricted-access" | "dedicated";

/**
 * Assignments tab for the builder resource editor — same flows as the
 * legacy RestrictionsTab (access mode with confirmation, priority
 * booking options, assignment tables and modals), reorganized into the
 * panel's compact sectioned layout.
 */
export interface BuilderResourceAssignmentsProps {
  resources: IResource[];
  mutateAll: (err: boolean) => void;
  /** Priority booking (additional access) is admin-only on the API. */
  canEditAdditionalAccess: boolean;
}

export const BuilderResourceAssignments: React.FC<
  BuilderResourceAssignmentsProps
> = ({ resources, mutateAll, canEditAdditionalAccess }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [showNonShared, setShowNonShared] = useState(false);
  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const { resourceIds, additionalAccess, refresh } = usePermissions(
    resources.map((resource) => resource.id),
    { includeAdditionalAccess: canEditAdditionalAccess }
  );
  const queryClient = useQueryClient();
  const permissions = useResourcesPermissions(resources, {
    includeAdditionalAccess: canEditAdditionalAccess
  });
  const accessChangeDisclosure = useDisclosure<{ type: AccessMode }>();

  // Dedicating makes a desk unbookable, so the reservations already on it have
  // to go. Load them while the confirmation is open so the admin sees whose
  // bookings they are about to cancel — and so `handleAccessChange` has the
  // exact list to delete. Dedicating is single-resource only (the card is
  // disabled otherwise), hence resources[0].
  const isDedicateConfirmOpen =
    accessChangeDisclosure.isOpen &&
    accessChangeDisclosure.data?.type === "dedicated";
  const dedicateTarget = resources[0];
  // Reservations are office wall-clock times, so the window has to be built in
  // the resource's own office timezone. Using the browser's calendar day would,
  // near a date boundary, put today's office bookings outside the range — they
  // would never be listed, and never cancelled, on a desk about to become
  // unbookable.
  const dedicateOfficeTz =
    (dedicateTarget?.office_id
      ? authenticatedUser?.offices?.byId?.[dedicateTarget.office_id]?.time_zone
      : undefined) ||
    getDeviceTZ() ||
    "UTC";
  const dedicateRangeStart = formatInTimeZone(
    new Date(),
    dedicateOfficeTz,
    "yyyy-MM-dd"
  );
  const dedicateRangeEnd = formatInTimeZone(
    addDays(new Date(), DEDICATE_LOOKAHEAD_DAYS),
    dedicateOfficeTz,
    "yyyy-MM-dd"
  );
  const {
    data: dedicateConflicts,
    // `isFetching`, not `isLoading`: reopening the confirmation for the same
    // resource and window serves the cached list while a refetch is still in
    // flight, and `isLoading` is false there. Confirming against that stale
    // list would miss bookings made since it was fetched — on a desk that is
    // about to stop being bookable.
    isFetching: isFetchingDedicateConflicts,
    error: dedicateConflictsError
  } = useResourceReservations({
    resourceId: dedicateTarget?.id,
    start: dedicateRangeStart,
    end: dedicateRangeEnd,
    tz: dedicateOfficeTz,
    enabled: isDedicateConfirmOpen
  });

  // A recurring booking arrives as one row per occurrence — collapse each
  // series so the prompt shows its pattern once and cancels it once.
  const dedicateConflictGroups = React.useMemo(
    () => groupResourceReservations(dedicateConflicts?.schedules ?? []),
    [dedicateConflicts]
  );
  const isDedicateBlocked =
    isFetchingDedicateConflicts ||
    !!dedicateConflictsError ||
    !!dedicateConflicts?.isTruncated;
  const invalidateSchedules = useInvalidateSchedules();
  const resourceContext = useResourceSettingsState();
  const { wrapApiRequest } = useStandardApiHandler();

  const allRestricted = areAll(
    resources.map((x) => x.is_open),
    false
  );
  const allDedicated = areAll(
    resources.map((x) => !!x.is_dedicated),
    true
  );
  // is_dedicated implies is_open === false, so dedicated wins the initial
  // mode over restricted — the two are mutually exclusive in the UI.
  const derivedAccessMode: AccessMode = allDedicated
    ? "dedicated"
    : !allRestricted
      ? "open-access"
      : "restricted-access";
  const [accessMode, setAccessMode] =
    React.useState<AccessMode>(derivedAccessMode);

  // Re-sync when the resources' server flags change (refetch after edits
  // made elsewhere) — the component stays mounted, so without this the
  // highlighted card could keep showing a mode the server already left.
  const accessSignature = resources
    .map((r) => `${r.id}:${r.is_open ? 1 : 0}${r.is_dedicated ? 1 : 0}`)
    .join("|");
  // Depending on accessSignature (not the derived mode) is intentional —
  // it stands in for the resources' flag content.
  React.useEffect(() => {
    setAccessMode(derivedAccessMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessSignature]);

  const form = useForm<AccessRestrictionsFormData>({
    defaultValues: {
      hasAdditionalAccess: false,
      startsOn: "0",
      startsAt: new Date(),
      allUsers: false
    }
  });
  const _hasAdditionalAccess = form.watch("hasAdditionalAccess");
  const hasAdditionalAccess =
    accessMode === "restricted-access" && _hasAdditionalAccess;
  const isAllUsersEnabled = form.watch("allUsers");

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourcesAll"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourceScheduledPermissionsAll"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.getResourceAdditionalAccess"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["resources.dedicatedUsers"]
      })
    ]);

  const handleAccessChange = wrapApiRequest(
    async () => {
      const mode = accessChangeDisclosure.data?.type;
      if (!mode) return;
      if (!resources.length) throw new Error("Resource not found");

      // Flip the resource state FIRST (dedicated requires is_open ===
      // false, same as restricted). If a PUT fails/throws here, nothing
      // has been deleted yet, so the server stays fully consistent —
      // still dedicated, owner intact.
      const newResources = resources.map((resource) => ({
        ...resource,
        is_open: mode === "open-access",
        is_dedicated: mode === "dedicated"
      }));
      await Promise.all(
        newResources.map((newResource) =>
          api.client.organizations.resourcePut(newResource.id, newResource)
        )
      );

      // A dedicated desk is nobody else's to book, so the reservations the
      // confirmation just listed have to go. This runs after the PUTs for the
      // same reason as the cleanup below: if it fails, the desk is dedicated
      // with stale bookings on it (visible, fixable) rather than bookings
      // deleted for a change that never landed.
      if (mode === "dedicated") {
        // One delete per entry: a recurring series is cancelled through its
        // master schedule, which removes every occurrence — including the ones
        // past the window we listed.
        // allSettled, not all: a rejected delete would otherwise abort before
        // the refresh below, while its siblings had already succeeded — the
        // map and agenda would keep showing bookings that are gone, and the
        // admin would get a generic error instead of a useful one.
        const deletions = await Promise.allSettled(
          getReservationCancelTargets(dedicateConflictGroups).map((target) =>
            api.client.schedules.delete({
              schedule: target.scheduleId,
              recurring: target.recurring,
              slug: authenticatedUser.slug ?? "",
              resourceId: target.resourceId
            })
          )
        );
        // Some deletes may have landed even if others did not, so refresh
        // before deciding what to report.
        await invalidateSchedules();
        const hasFailedDeletion = deletions.some(
          (deletion) => deletion.status === "rejected" || !deletion.value.ok
        );
        if (hasFailedDeletion) {
          await invalidateAll();
          // Returned, not thrown: this function runs through wrapApiRequest,
          // which catches a throw, logs it, and resolves to nothing — the
          // admin would see the dialog just sit there with no explanation,
          // even though the desk WAS already dedicated. ConfirmModal reads a
          // truthy return value as the error to display, so this is what
          // actually surfaces it.
          return new Error(
            "Desk dedicated, but some existing reservations could not be cancelled — please review them"
          );
        }
      }

      // Then clear the now-stale dedicated-user links. A failure here
      // leaves an orphaned link on a resource that is no longer dedicated
      // (harmless vs. "dedicated with no owner") — surface it so the
      // admin retries.
      if (mode !== "dedicated") {
        try {
          for (const resource of resources) {
            if (!resource.is_dedicated) continue;
            const current =
              await api.client.organizations.resourceDedicatedUsersGet(
                resource.id
              );
            const deletions = await Promise.all(
              normalizeDedicatedUsers(current.result).map((user) =>
                api.client.organizations.resourceDedicatedUserDelete(
                  resource.id,
                  user.id
                )
              )
            );
            if (deletions.some((deletion) => !deletion.ok)) {
              throw new Error(
                `Access updated, but the dedicated user link on ${resource.name} could not be removed — please try again`
              );
            }
          }
        } catch (error) {
          // The PUTs already landed — refresh the caches so the UI shows
          // the real server state even though the cleanup failed.
          await invalidateAll();
          throw error;
        }
      }
      newResources.forEach((newResource) => {
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: newResource
        });
      });
      // Only flip the UI mode after every API call succeeded — an early
      // flip would show the new mode while the server kept the old state.
      setAccessMode(mode);
      await invalidateAll();
      await mutateAll(true);
      accessChangeDisclosure.close();
      return;
    },
    resources.length === 1
      ? "Resource access updated"
      : "Resources access updated"
  );

  const handleUpdateOptions = form.handleSubmit(
    wrapApiRequest(async (data: AccessRestrictionsFormData) => {
      if (!resources.length) throw new Error("Resource not found");
      await Promise.all(
        resources.map((resource) =>
          api.client.organizations.setResourceAdditionalAccessOptions(
            resource.id,
            {
              enabled: data.hasAdditionalAccess,
              starts_on: Number(data.startsOn),
              starts_at: `${data.startsAt.getHours()}:${data.startsAt.getMinutes()}`,
              all_users: data.allUsers
            }
          )
        )
      );
      await invalidateAll();
    }, "Resource access updated")
  );

  // Hydrate priority-booking options from the loaded additional-access
  // config, exactly like the legacy tab.
  const sanitizedAdditionalAccess = JSON.stringify(additionalAccess);
  const sanitizedResource = JSON.stringify(resources);
  React.useEffect(() => {
    if (!additionalAccess || !additionalAccess[0].length) return;
    if (!resources[0]) return;
    if (sanitizedAdditionalAccess) {
      const nDate = getTimeDateObjectFromString(
        additionalAccess?.[0]?.[0]?.starts_at || ""
      );
      const allHasAllUsers = areAll(
        additionalAccess.map((x) => x[0]?.all_users),
        true
      );
      form.reset({
        hasAdditionalAccess: additionalAccess[0][0].enabled,
        startsOn: String(additionalAccess[0][0].starts_on || 0),
        startsAt: nDate,
        allUsers: allHasAllUsers
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedAdditionalAccess, sanitizedResource]);

  const accessCards: {
    mode: AccessMode;
    icon: React.ReactNode;
    title: string;
    description: string;
  }[] = [
    {
      mode: "open-access",
      icon: <Users size={15} />,
      title: "Open Access",
      description:
        resources.length === 1
          ? "Anybody can reserve this resource."
          : "Anybody can reserve these resources."
    },
    {
      mode: "restricted-access",
      icon: <Lock size={15} />,
      title: "Restricted Access",
      description:
        resources.length === 1
          ? "Only those assigned can reserve it."
          : "Only those assigned can reserve them."
    },
    {
      mode: "dedicated",
      icon: <UserCheck size={15} />,
      title: "Dedicated",
      description:
        resources.length === 1
          ? "Belongs to specific people. Nobody else can book it."
          : "Select a single resource to dedicate it."
    }
  ];

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto pb-4">
      <div>
        <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
          Access
        </p>
        <div className="flex flex-col gap-2">
          {accessCards.map((card) => {
            const isActive = accessMode === card.mode;
            // Dedicating is a one-resource affair — a person owns a
            // specific desk, not a batch.
            const isDisabled =
              card.mode === "dedicated" && resources.length > 1;
            return (
              <button
                key={card.mode}
                type="button"
                disabled={isDisabled}
                onClick={() =>
                  !isActive && accessChangeDisclosure.open({ type: card.mode })
                }
                className={cn(
                  "flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors",
                  isActive
                    ? "border-primary bg-primary/5"
                    : "border-neutral-200 hover:border-neutral-300",
                  isDisabled &&
                    "cursor-not-allowed opacity-50 hover:border-neutral-200"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex-none",
                    isActive ? "text-primary" : "text-neutral-400"
                  )}
                >
                  {card.icon}
                </span>
                <span className="min-w-0">
                  <span
                    className={cn(
                      "block font-medium text-sm",
                      isActive ? "text-neutral-900" : "text-neutral-700"
                    )}
                  >
                    {card.title}
                  </span>
                  <span className="block text-[11px] text-neutral-400">
                    {card.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {accessMode === "dedicated" && resources.length === 1 && (
          <DedicatedOwnersSection
            resource={resources[0]}
            onChanged={() => mutateAll(false)}
          />
        )}
      </div>

      {/* Priority booking and assignments only make sense for bookable
          pools — a dedicated resource belongs to its owners, so both
          sections disappear entirely in dedicated mode. */}
      {accessMode !== "dedicated" && (
        <>
          {canEditAdditionalAccess ? (
            <div>
              <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
                Priority Booking
              </p>
              <HookFormToggleInput
                form={form}
                name="hasAdditionalAccess"
                label="Secondary group access"
                description="If the primary group hasn't reserved, a secondary group can gain access at a time you choose."
                onChange={() => handleUpdateOptions()}
                // Priority booking only applies to restricted pools — open
                // resources have no primary group, dedicated ones aren't
                // bookable at all.
                disabled={accessMode !== "restricted-access"}
                togglePosition="right"
              />
              {hasAdditionalAccess && (
                <div className="mt-2 flex flex-col gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
                  <div className="flex items-start gap-3">
                    <HookFormUnifiedSelectInput
                      form={form}
                      name="startsOn"
                      label="Days"
                      items={STARTS_ON_ITEMS}
                      className="flex-1"
                      onChange={() => handleUpdateOptions()}
                    />
                    <HookFormTimeSelectDate
                      form={form}
                      name="startsAt"
                      label="Time"
                      interval={15}
                      placeholder="Select time"
                      onChange={() => handleUpdateOptions()}
                    />
                  </div>
                  <HookFormCheckboxInput
                    form={form}
                    name="allUsers"
                    label={
                      <>
                        Add
                        <span className="mx-1 font-bold capitalize">
                          All {authenticatedUser?.organization?.name} Users
                        </span>
                        to the secondary group
                      </>
                    }
                    onChange={() => handleUpdateOptions()}
                    checkboxPosition="left"
                  />
                </div>
              )}
            </div>
          ) : null}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
                Assignments
              </p>
              <AddAssignmentButton
                resources={resources}
                hasAdditionalAccess={hasAdditionalAccess}
                isAllUsersEnabled={isAllUsersEnabled}
                onRefresh={() => {
                  refresh();
                  mutateAll(false);
                }}
              />
            </div>

            {permissions.shared?.length ? (
              <div className="overflow-x-auto">
                <SharedPermissionTable
                  setOperationalModal={setOperationModal}
                  data={permissions.shared}
                  inGroups={permissions.inGroups}
                  onDeleted={() => mutateAll(false)}
                />
              </div>
            ) : (
              <p className="rounded-md border border-neutral-200 border-dashed px-3 py-4 text-center text-neutral-400 text-xs">
                No assignments yet. Assign teams or users to restrict access or
                require approval.
              </p>
            )}

            {resources.length > 1 && permissions.rest.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowNonShared((v) => !v)}
                  className="inline-flex items-center gap-1 font-medium text-neutral-500 text-xs hover:text-neutral-700"
                >
                  {showNonShared ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                  Non-shared assignments ({permissions.rest.length})
                </button>
                {showNonShared && (
                  <div className="mt-2 overflow-x-auto">
                    <PermissionTable
                      setOperationalModal={setOperationModal}
                      data={permissions.rest}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={accessChangeDisclosure.isOpen}
        onOpenChange={accessChangeDisclosure.setOpen}
        onConfirm={handleAccessChange}
        confirmProps={{
          disabled: isDedicateConfirmOpen && isDedicateBlocked
        }}
        confirmLabel={
          isDedicateConfirmOpen && dedicateConflictGroups.length > 0
            ? "Dedicate and cancel"
            : "Confirm"
        }
        title={
          isDedicateConfirmOpen
            ? "Make this a dedicated desk?"
            : "Change Resource's Access Type"
        }
        message={
          isDedicateConfirmOpen ? (
            <DedicateResourceWarning
              resourceName={dedicateTarget?.name || "This resource"}
              groups={dedicateConflictGroups}
              isLoading={isFetchingDedicateConflicts}
              error={dedicateConflictsError}
              isTruncated={!!dedicateConflicts?.isTruncated}
            />
          ) : (
            <>
              Are you sure you want to change the access type of the selected
              resources to{" "}
              <strong>{accessChangeDisclosure.data?.type || ""}</strong> ?
            </>
          )
        }
      />
      <ToggleIsApprovablePermission
        isOpen={
          operationModal?.operation === "is_approvable" &&
          operationModal.applyTo.length > 0
        }
        onOpenChange={(open) => {
          if (!open) {
            setOperationModal(null);
            mutateAll(false);
          }
        }}
        resourceIds={resourceIds}
        permissions={operationModal?.applyTo ?? []}
      />
    </div>
  );
};
